import axios from 'axios';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true
});

// Helper to check if we are currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

// Request interceptor to add access token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Prevent infinite loops
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        // Check if error is 401 and we haven't retried yet
        if (error.response && error.response.status === 401 && !originalRequest._retry) {

            // If we are already refreshing, add to queue
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem('refreshToken');

            // If no refresh token, logout
            if (!refreshToken) {
                isRefreshing = false;
                // Let component/context handle logout redirect via event or error bubbling
                return Promise.reject(error);
            }

            try {
                const response = await axios.post(`${API_URL}/api/auth/jwt/refresh`, {
                    refreshToken
                }, {
                    withCredentials: true
                });

                const { accessToken, refreshToken: newRefreshToken } = response.data;

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

                processQueue(null, accessToken);
                isRefreshing = false;

                return api(originalRequest);
            } catch (err) {
                processQueue(err, null);
                isRefreshing = false;

                // Clear tokens on refresh failure
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');

                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);

export default api;
